import React from "react";
import Icon from "./Icon";
import { Avatar } from "./ApplicantRow";
import { decideInstructorSignups } from "../../../api/backend";
import { roleLabel, sessionLabel, formatDay, NOTE_MAX } from "./signupsModel";

const reasonOf = (e, fallback) => e?.response?.data?.message || fallback;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const where = (item) => `${sessionLabel(item.group)}, ${formatDay(item.group.sessionDate)}`;

/**
 * One instructor who asked for several sessions of the same course and role: decide them together. Approve skips any
 * session that would be refused (conflict, missing AHA ID, slot filled) and says so; Reject takes one optional comment
 * for the instructor. The instructor gets one email either way. Per-slot buttons below still work one at a time.
 */
export default function WholeClassRequests({ clusters, tenantId, onReload, announce = () => {} }) {
  const [asking, setAsking] = React.useState(null); // { key, mode: "approve" | "reject" }
  const [note, setNote] = React.useState("");
  const [busyKey, setBusyKey] = React.useState(null);
  const [outcome, setOutcome] = React.useState(null);

  const ask = (key, mode) => { setAsking({ key, mode }); setNote(""); setOutcome(null); };
  const cancel = () => { setAsking(null); setNote(""); };

  const run = async (cluster, mode) => {
    const approve = mode === "approve";
    const targets = approve ? cluster.approvable : cluster.items;
    const skipped = approve ? cluster.items.filter((i) => !cluster.approvable.includes(i)) : [];
    setBusyKey(cluster.key);
    setAsking(null);
    try {
      const data = await decideInstructorSignups(targets.map((i) => i.signupId), approve ? "APPROVE" : "UNAPPROVE", note, tenantId);
      const byId = new Map((data?.results ?? []).map((r) => [r.signupId, r]));
      const problems = [
        ...skipped.map((i) => `${where(i)}: ${i.applicant.blockerMessage || "can't be approved right now"}`),
        ...targets.filter((i) => byId.get(i.signupId)?.status !== "APPLIED")
          .map((i) => `${where(i)}: ${byId.get(i.signupId)?.reason || "not changed"}`),
      ];
      const done = targets.length - targets.filter((i) => byId.get(i.signupId)?.status !== "APPLIED").length;
      const text = `${plural(done, "session")} ${approve ? "approved" : "rejected"} for ${cluster.instructorName}.`;
      setOutcome({ text, problems, tone: done === 0 ? "err" : "ok" });
      announce(`${text}${problems.length ? ` ${plural(problems.length, "session")} could not be changed.` : ""}`);
    } catch (e) {
      const text = `Couldn't ${approve ? "approve" : "reject"} ${cluster.instructorName}'s sessions.`;
      setOutcome({ text, problems: [reasonOf(e, "Please try again.")], tone: "err" });
      announce(text);
    } finally {
      setBusyKey(null);
      setNote("");
      onReload();
    }
  };

  if (clusters.length === 0 && !outcome) return null;

  return (
    <section className="isg-wc" aria-label="Whole-class requests">
      {outcome && (
        <div className={`isg-wc-out ${outcome.tone}`} role={outcome.tone === "err" ? "alert" : "status"} data-testid="isg-cluster-outcome">
          <div>
            <b>{outcome.text}</b>
            {outcome.problems.length > 0 && (
              <ul>{outcome.problems.map((p) => <li key={p}>{p}</li>)}</ul>
            )}
          </div>
          <button type="button" className="isg-btn isg-btn-o isg-btn-sm" onClick={() => setOutcome(null)}>Dismiss</button>
        </div>
      )}
      {clusters.map((c) => {
        const mine = asking?.key === c.key ? asking.mode : null;
        const busy = busyKey === c.key;
        const skipped = c.items.length - c.approvable.length;
        return (
          <div className="isg-wc-card" key={c.key} data-testid={`isg-cluster-${c.key}`} role="group"
            aria-label={`${c.instructorName}, ${plural(c.items.length, "session")} of ${c.courseTitle}`}>
            <div className="isg-wc-h">
              <Avatar name={c.instructorName} />
              <div className="t">
                <b>{c.instructorName} asked for {plural(c.items.length, "session")}</b>
                <span>{c.courseNumber ? `${c.courseNumber} · ` : ""}{c.courseTitle} · {roleLabel(c.teachingRole)}</span>
              </div>
              <div className="a">
                <button type="button" className="isg-btn isg-btn-p isg-btn-sm" disabled={busy || c.approvable.length === 0}
                  title={c.approvable.length === 0 ? "None of these sessions can be approved right now" : undefined}
                  onClick={() => ask(c.key, "approve")}>
                  <Icon name="check" size={12} />Approve whole class
                </button>
                <button type="button" className="isg-btn isg-btn-o isg-btn-sm" disabled={busy} onClick={() => ask(c.key, "reject")}>
                  Reject whole class
                </button>
              </div>
            </div>
            <ul className="isg-wc-s">
              {c.items.map((i) => (
                <li key={i.signupId}>
                  <b>{formatDay(i.group.sessionDate)}</b> <span>{sessionLabel(i.group)}</span>
                  {i.applicant.blockerMessage && <em> · {i.applicant.blockerMessage}</em>}
                </li>
              ))}
            </ul>
            {mine === "approve" && (
              <div className="isg-cf" role="group" aria-label="Confirm whole-class approval">
                <p>
                  Approve <b>{c.instructorName}</b> for {c.approvable.length} of {plural(c.items.length, "session")} as {roleLabel(c.teachingRole).toLowerCase()}?
                  <small>
                    {skipped
                      ? `${plural(skipped, "session")} can't be approved and will be skipped. They get one email for the rest.`
                      : "They get one email listing every session."}
                  </small>
                </p>
                <button type="button" className="isg-btn isg-btn-p isg-btn-sm" data-testid="isg-cluster-approve-go" onClick={() => run(c, "approve")}>
                  Approve {plural(c.approvable.length, "session")}
                </button>
                <button type="button" className="isg-btn isg-btn-g isg-btn-sm" onClick={cancel}>Cancel</button>
              </div>
            )}
            {mine === "reject" && (
              <div className="isg-cf isg-rj" role="group" aria-label="Confirm whole-class rejection">
                <p>
                  Reject <b>{c.instructorName}</b> for all {plural(c.items.length, "session")}?
                  <small>One email goes to them, and your comment (if you add one) is shown on their My Requests tab.</small>
                </p>
                <label className="isg-rj-l" htmlFor={`isg-wc-note-${c.key}`}>Comments for the instructor (optional)</label>
                <textarea id={`isg-wc-note-${c.key}`} data-testid="isg-cluster-note" rows={3} maxLength={NOTE_MAX} value={note}
                  placeholder="e.g. We already have a lead for these dates." onChange={(e) => setNote(e.target.value)} />
                <span className="isg-rj-c" aria-live="polite">{note.length}/{NOTE_MAX}</span>
                <button type="button" className="isg-btn isg-btn-p isg-btn-sm" data-testid="isg-cluster-reject-go" onClick={() => run(c, "reject")}>
                  Send decision
                </button>
                <button type="button" className="isg-btn isg-btn-g isg-btn-sm" onClick={cancel}>Cancel</button>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
